"use client";

import clsx from "clsx";
import {
  CHAT_ENTITY_CARD_SHELL_CLASS,
  formatDataTableCellValue,
  formatDataTableColumnHeader,
  isNumericTableCell,
} from "./chatAnalyticsEmbedUi";
import { ChatEmbedFooter } from "./ChatEmbedFooter";
import type { ChatDataTableEmbed } from "./types";

type ChatDataTableCardProps = {
  embed: ChatDataTableEmbed;
  density?: "default" | "toolDetail";
};

export function ChatDataTableCard({ embed, density = "default" }: ChatDataTableCardProps) {
  const columns = embed.columns;
  const rows = embed.rows;
  const compact = density === "toolDetail";

  return (
    <div
      className={clsx(compact ? "text-[11px]" : CHAT_ENTITY_CARD_SHELL_CLASS, !compact && "text-sm")}
      data-testid="chat-embed-data-table"
      data-density={compact ? "toolDetail" : "default"}
    >
      {embed.title && !compact ? (
        <div className="mb-2 px-0.5 text-[12px] font-medium leading-tight text-slate-700 dark:text-slate-200">
          {embed.title}
        </div>
      ) : null}
      {rows.length === 0 ? (
        <div
          className={
            compact
              ? "text-[11px] text-slate-500 dark:text-slate-500"
              : "px-0.5 text-[12px] text-slate-500 dark:text-slate-400"
          }
        >
          No rows
        </div>
      ) : (
        <div
          className={
            compact
              ? "overflow-x-auto"
              : "overflow-x-auto rounded-md border border-slate-200/60 bg-white/80 dark:border-slate-700/45 dark:bg-slate-900/35"
          }
        >
          <table className="w-full min-w-[10rem] border-collapse text-left">
            <thead>
              <tr
                className={
                  compact
                    ? "border-b border-slate-200/50 dark:border-slate-700/40"
                    : "border-b border-slate-200/70 bg-slate-50/90 dark:border-slate-700/50 dark:bg-slate-800/50"
                }
              >
                {columns.map((column) => (
                  <th
                    key={column}
                    className={
                      compact
                        ? "px-1.5 py-0.5 text-[10px] font-normal uppercase tracking-wide text-slate-500 dark:text-slate-500"
                        : "px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
                    }
                  >
                    {formatDataTableColumnHeader(column)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={
                    compact
                      ? "border-b border-slate-100/60 last:border-b-0 dark:border-slate-800/50"
                      : "border-b border-slate-100/80 last:border-b-0 dark:border-slate-800/80"
                  }
                >
                  {columns.map((column) => {
                    const display = formatDataTableCellValue(row[column]);
                    const numeric = isNumericTableCell(display);
                    return (
                      <td
                        key={column}
                        className={clsx(
                          compact
                            ? "px-1.5 py-0.5 text-[11px] text-slate-700 dark:text-slate-300"
                            : "px-3 py-2 text-[12px] text-slate-800 dark:text-slate-100",
                          numeric && "tabular-nums text-right",
                        )}
                      >
                        {display}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ChatEmbedFooter as_at={embed.as_at} units={embed.units} density={density} />
    </div>
  );
}
